// libs
import Vue from 'vue';
import VueRouter from 'vue-router';
import VueBodyClass from 'vue-body-class';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// components
import Cart from '@/views/Cart.vue';
import Home from '@/views/Home.vue';
import Login from '@/views/Login.vue';
import NotFound from '@/views/PageNotFound.vue';
import Redirect from '@/views/Redirect.vue';
import Onboarding from '@/views/Onboarding.vue';
import NewPassword from '@/views/NewPassword.vue';
import ResetPassword from '@/views/ForgotPassword.vue';
import Quiz from '@/components/partials/Quiz';

import { onboardingRoutes } from './onboarding.js';
import store from '@/store'

Vue.use(VueRouter);

// services
import { AuthService } from '@/services/auth.service';
import { OnboardingService } from '@/services/onboarding.service';

// utils
import { getURLParams } from '@/utils/urlParams.js';
import cookies from '@/utils/cookies';
import { COOKIES } from '@/constants';

const routes = [
  {
    path: '*',
    redirect: '/404',
    meta: {
      transition: 'overlay-up-down',
    },
  },
  {
    path: '/404',
    name: 'NotFound',
    component: NotFound,
    meta: {
      title: 'Page not Found',
    },
  },
  {
    path: '/playground',
    name: 'playground',
    component: () => import('@/views/Playground'),
  },
  {
    // id act as user credentials for example
    // /redirect/myidtoken, /redirect/myidnumber,
    path: '/redirect/',
    name: 'redirect',
    component: Redirect,
    meta: {
      transition: 'fade',
      title: 'Redirection in process...',
    },
  },
  {
    path: '/website/',
    async beforeEnter(to, from, next) {
      try {
        await AuthService.logout();
      } catch (error) {
        console.error(error);
      } finally {
        const params = getURLParams();
        location.href = `${process.env.VUE_APP_WEBSITE_URL}${params ? `?${params}` : ''}`;
        next(false);
      }
    },
    meta: {
      title: 'Redirection in progress...',
    },
  },
  {
    path: '/website/contact',
    beforeEnter(to, from, next) {
      location.href = `${process.env.VUE_APP_WEBSITE_URL}/contact`;
      next(false);
    },
    meta: {
      title: 'Redirection in progress...',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      transition: 'fade-in-left',
      title: 'Login',
    },
  },
  {
    path: '/cart',
    name: 'cart',
    component: Cart,
    meta: {
      transition: 'fade',
      title: 'Cart',
      bodyClass: 'cart',
    },
  },
  {
    path: '/quiz',
    name: 'quiz',
    component: Quiz,
    meta: {
      transition: 'fade',
    },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ResetPassword,
    meta: {
      transition: 'fade-in-left',
      title: 'Forgot Password',
    },
  },
  {
    path: '/new-password',
    name: 'new-password',
    component: NewPassword,
    meta: {
      transition: 'fade-in-left',
      title: 'New Pasword',
    },
  },
  {
    path: '/',
    name: 'onboarding',
    component: Onboarding,
    async beforeEnter(to, from, next) {
      const token = localStorage.getItem('token');
      if (!token) {
        return next('/login');
      }
      try {
        const { data } = await OnboardingService.getPatientOnboarding();
        if (data?.onboardingstatus === 'DONE') {
          next('/home');
        } else {
          window.location.href = '/eligibility';
        }
      } catch (err) {
        next('/login');
      }
    },
    meta: {
      bodyClass: 'getting-started',
      title: 'Onboarding',
    },
  },
  {
    path: '/home',
    name: 'index',
    component: Home,
    beforeEnter(to, from, next) {
      const head = document.head;
      const sas = document.getElementById('shareasale');
      if (sas) {
        head.removeChild(sas);
      }
      next();
    },
    children: [
      {
        path: '/home',
        name: 'home',
        component: () => import('@/components/dashboard/Dashboard.vue'),
        meta: {
          transition: 'fade',
          title: 'Home',
        },
      },
      {
        // id act as user credentials
        path: '/treatment',
        name: 'treatment',
        component: () => import('@/components/dashboard/Treatment.vue'),
        meta: {
          transition: 'fade',
          title: 'Treatment',
        },
      },
      {
        path: '/messages',
        name: 'messages',
        component: () =>
          import('@/components/messages/MessageVersionSwitcher.vue'),
        meta: {
          transition: 'fade',
          title: 'Message',
          bodyClass: 'message-page',
        },
      },
      {
        path: '/message',
        redirect: '/messages/doctor',
      },
      {
        path: '/follow-up',
        redirect: '/home',
      },
      {
        path: '/messages/doctor',
        name: 'message-doctor',
        component: () => import('@/components/dashboard/Message.vue'),
        meta: {
          transition: 'fade',
          title: 'Message',
          bodyClass: 'message-page',
        },
      },
      {
        path: '/messages/admin',
        name: 'message-admin',
        redirect: '/messages/patient_services',
      },
      {
        path: '/messages/patient_services',
        name: 'message-patient-services',
        component: () => import('@/components/messages/AdminChatBase.vue'),
        meta: {
          transition: 'fade',
          title: 'Message',
          bodyClass: 'message-page',
        },
      },
      {
        path: '/support/:category?/:action?',
        name: 'support',
        component: () => import('@/components/dashboard/Support.vue'),
        meta: {
          transition: 'fade',
          title: 'Help Center',
          bodyClass: 'support',
        },
      },
      {
        path: '/subscription/:id',
        name: 'subscription',
        component: () => import('@/components/partials/Subscription.vue'),
        meta: {
          transition: 'fade',
          title: 'Subscription',
        },
      },
      {
        path: '/order/:id',
        name: 'order',
        component: () => import('@/components/partials/Order.vue'),
        meta: {
          transition: 'fade',
          title: 'My Order',
        },
      },
      {
        path: '/your-account',
        name: 'your-account',
        component: () => import('@/components/account/Account.vue'),
        meta: {
          transition: 'fade',
          title: 'Your Account',
        },
      },
      {
        path: '/account-settings',
        name: 'account-settings',
        component: () => import('@/components/account/Profile.vue'),
        meta: {
          transition: 'fade',
          title: 'Account Settings',
        },
        children: [
          {
            name: 'billing-details',
            path: '/account-settings/billing-details',
            meta: {
              title: 'Account Settings',
            },
          },
          {
            name: 'account-identity-verification',
            path: '/account-settings/identity-verification',
            meta: {
              title: 'Account Settings',
            },
          },
          {
            name: 'billing-address',
            path: '/account-settings/billing-address',
            meta: {
              title: 'Billing Address',
            },
          },
          {
            name: 'shipping-address',
            path: '/account-settings/shipping-address',
            meta: {
              title: 'Shipping Address',
            },
          },
          {
            name: 'update-email',
            path: '/account-settings/email',
            meta: {
              title: 'Update Email',
            },
          },
        ],
      },
      {
        path: '/medical-information',
        name: 'medical-information',
        component: () => import('@/components/account/MedicalInformation.vue'),
        meta: {
          transition: 'fade',
          title: 'Medical Information',
        },
      },
      {
        path: '/insurance-documents',
        name: 'insurance-documents',
        component: () => import('@/components/account/InsuranceDocuments.vue'),
        meta: {
          transition: 'fade',
          title: 'Insurance Documents',
        },
      },
      {
        path: '/contact-us',
        name: 'contact-us',
        component: () => import('@/components/partials/ContactUs.vue'),
        meta: {
          transition: 'fade',
          title: 'Contact Care Team',
        },
      },
      {
        path: '/cancel-subscription',
        name: 'cancel-subscription',
        component: () => import('@/components/partials/CancelSubscription.vue'),
        meta: {
          transition: 'fade',
          title: 'Cancel Subscription',
        },
      },
      {
        path: '/referral/',
        component: () => import('@/components/dashboard/Referrals'),
        meta: {
          transition: 'fade',
          title: 'Refer a Friend',
        },
        beforeEnter(to, from, next) {
          if (to.query.refcode) {
            cookies.set('referralToken', to.query.refcode);
            if (localStorage.getItem('accessToken')) {
              next();
            } else {
              window.location.href = '/eligibility';
            }
          }
          next();
        },
      },
    ],
    meta: {
      requiresAuth: false,
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
});

const vueBodyClass = new VueBodyClass(routes);
const DEAFULT_TITLE = 'Winona';

router.beforeEach(async (to, from, next) => {
  if (to.name === 'support' && !localStorage.getItem('token') && !to.query.token) {
    return next({ name: 'login', query: { redirectTo: to.fullPath } });
  }

  vueBodyClass.guard(to, next);

  // check if a datadog recording should start
  const allOnboardingRoutes = onboardingRoutes.concat('/eligibility');
  if (allOnboardingRoutes.concat('/eligibility').includes(to.path)) {
    if (
      process.env.NODE_ENV == 'production' &&
      typeof Vue.prototype.$ddRum != 'undefined'
    ) {
      Vue.prototype.$ddRum.datadogRum.startSessionReplayRecording();
    }
  }

  if (to.query.token) {
    localStorage.setItem('accessToken', `${to.query.token}`);
    localStorage.setItem('token', `Bearer ${to.query.token}`);
    const { data } = await AuthService.getTokenFromOTT();
    if (data) {
      localStorage.setItem('accessToken', `${data.token}`);
      localStorage.setItem('token', `Bearer ${data.token}`);
      localStorage.setItem('isAuthenticated', true);
      localStorage.setItem('refreshToken', true);
    }
  }

  if (to.query[COOKIES.TRACKING.GOOGLE_CLICK_ID.PARAM]) {
    const c = COOKIES.TRACKING.GOOGLE_CLICK_ID;
    cookies.set(c.NAME, to.query[c.PARAM], c.TTL, null, COOKIES.COOKIE_DOMAIN);
  }

  if (to.query[COOKIES.TRACKING.TIKTOK_CLICK_ID.PARAM]) {
    const c = COOKIES.TRACKING.TIKTOK_CLICK_ID;
    cookies.set(c.NAME, to.query[c.PARAM], c.TTL, null, COOKIES.COOKIE_DOMAIN);
  }

  if (to.query[COOKIES.TRACKING.FB_CLICK_ID.PARAM]) {
    const c = COOKIES.TRACKING.FB_CLICK_ID;
    cookies.set(c.NAME, to.query[c.PARAM], c.TTL, null, COOKIES.COOKIE_DOMAIN);
  }

  if (to.query[COOKIES.TRACKING.PINTEREST_CLICK.PARAM]) {
    const c = COOKIES.TRACKING.PINTEREST_CLICK;
    cookies.set(c.NAME, to.query[c.PARAM], c.TTL, null, COOKIES.COOKIE_DOMAIN);
  }

  if (to.query[COOKIES.TRACKING.TWITTER_CLICK.PARAM]) {
    const c = COOKIES.TRACKING.TWITTER_CLICK;
    cookies.set(c.NAME, to.query[c.PARAM], c.TTL, null, COOKIES.COOKIE_DOMAIN);
  }

  if (!cookies.get('winona_deduplicationID')) {
    cookies.set('winona_deduplicationID', uuidv4());
  }

  // * FSA HSA sessionPromo
  const sessionPromo = cookies.get('sessionPromo') || to.query.sessionPromo

  if (sessionPromo) {
    const couponResult = await OnboardingService.checkCoupon(
      sessionPromo
    );

    const data = await store.dispatch('onboarding/getPatientOnboarding');
    if (data.onboardingstatus !== 'DONE' && couponResult?.data?.id) {
      await OnboardingService.applyCoupon({
        couponid: sessionPromo,
      });
      cookies.remove('promocode')
    }
  }

  else if (to.query.promocode || cookies.get('promocode')) {
    if (!to.query.ec_id && !cookies.get('promocodeExistingID')) {
      const promocode = to.query.promocode || cookies.get('promocode');
      try {
        await OnboardingService.checkCoupon(promocode);
        // For those who are already logged in
        if (window.localStorage.getItem('accessToken')) {
          // * Temporarily opt for hardcoded values
          const blossomPromoCodes = [
            'BFBLOSSOM40',
            'BFBLOSSOM40FIXED',
            'BLACKFRIDAY2022',
            'CYBERMONDAY2022',
            'BFBLOSSOM10',
          ];
          if (!blossomPromoCodes.includes(promocode)) {
            const data = await store.dispatch('onboarding/getPatientOnboarding');
            if (
              data.onboardingstatus !== 'DONE' &&
              !data.usedreferralcode
            ) {
              await OnboardingService.applyCoupon({
                couponid: promocode,
              });
              cookies.remove('promocode');
            }
          }
        }

        if (to.query.promocode && !window.localStorage.getItem('accessToken')) {
          cookies.set('promocode', to.query.promocode, '48h', null, '.bywinona.com');
        }
      } catch (err) {
        if (err == 'Error: Coupon does not exist.') {
          alert(
            'The coupon you entered does not exist, please check the code you entered.'
          );
          cookies.remove('promocode');
        } else if (err == 'Error: Unauthorized request') {
          window.localStorage.removeItem('token');
          window.localStorage.removeItem('accessToken');
        } else if (err == 'Error: You\'ve already completed this step') {
          const newRouteQuery = _.omit(to.query, 'promocode');
          router.replace({ query: newRouteQuery });
          cookies.remove('promocode');
        } else {
          alert(err);
        }
      }
    } else {
      if (!cookies.get('promocode')) {
        cookies.set('promocode', to.query.promocode, '48h', null, '.bywinona.com');
      }
      if (!cookies.get('promocodeExistingID')) {
        cookies.set('promocodeExistingID', to.query.ec_id);
      }
    }
  }

  // * Share a Sale
  if (to.query.sscid) {
    cookies.set('sas_m_awin', to.query.sscid);
  }

  // irclickid
  if (to.query[COOKIES.TRACKING.IMPACT_CLICK_ID.PARAM]) {
    const c = COOKIES.TRACKING.IMPACT_CLICK_ID;
    console.log('irclickid detected: ', to.query[c.PARAM]);
    cookies.set(c.NAME, to.query[c.PARAM], c.TTL, null, COOKIES.COOKIE_DOMAIN);
  }

  // Voluum click ID
  if (to.query.click_id) {
    const tbclidObj = { taboolaClickID: to.query.click_id };
    console.log('Click ID detected: ', tbclidObj);
    cookies.set('taboolaClickID', tbclidObj);
  }

  if (to.query[COOKIES.TRACKING.TABOOLA_CLICK_ID.PARAM]) {
    const c = COOKIES.TRACKING.TABOOLA_CLICK_ID;
    console.log('tbclid detected: ', to.query[c.PARAM]);
    cookies.set(c.NAME, to.query[c.PARAM], c.TTL, null, COOKIES.COOKIE_DOMAIN);
  }

  document.title = DEAFULT_TITLE + ' | ' + to.meta.title;
  if (to.path == '/support') {
    next();
  } else if (
    to.matched.some(record => record.meta.requiresAuth) &&
    !JSON.parse(localStorage.getItem('accessToken'))
  ) {
    next({ name: 'login' });
  } else {
    next();
  }
});

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err && err.name !== 'NavigationDuplicated') {
      throw err;
    }
  });
};

router.beforeEach((to, from, next) => {
  store.commit('global/SET_STATE', {
    state: 'previousRouteName',
    value: from.name
  })

  next()
})

export default router;